import React, { lazy, Suspense, useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AccountLayout } from '../components/account/AccountLayout';
import { LoginSecuritySection } from '../components/account/LoginSecuritySection';
import { AddressesSection } from '../components/account/AddressesSection';
import { OrdersSection } from '../components/account/OrdersSection';
import { WalletSection } from '../components/account/WalletSection';
import { LoyaltyPointsSection } from '../components/account/LoyaltyPointsSection';
import { WishlistSection } from '../components/account/WishlistSection';
import { ReturnsSection } from '../components/account/ReturnsSection';
import { CouponsSection } from '../components/account/CouponsSection';
import { PaymentRequestsSection } from '../components/account/PaymentRequestsSection';
import { PaymentRequestDetailsSection } from '../components/account/PaymentRequestDetailsSection';
import { SEO } from '../components/shared/SEO';
import { UserProfileCard } from '../components/account/UserProfileCard';
import { LoyaltyCard } from '../components/account/LoyaltyCard';
import { ReferralCard } from '../components/account/ReferralCard';
import { AccountMenuList } from '../components/account/AccountMenuList';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { useStore } from '../lib/store';
const AuthBottomSheet = lazy(() => import('../components/modals/AuthBottomSheet').then(module => ({
  default: module.AuthBottomSheet
})));
const LoyaltyOnboardingBottomSheet = lazy(() => import('../components/modals/LoyaltyOnboardingBottomSheet').then(module => ({
  default: module.LoyaltyOnboardingBottomSheet
})));
const EditProfileBottomSheet = lazy(() => import('../components/account/EditProfileBottomSheet').then(module => ({
  default: module.EditProfileBottomSheet
})));
const ChangePasswordBottomSheet = lazy(() => import('../components/account/ChangePasswordBottomSheet').then(module => ({
  default: module.ChangePasswordBottomSheet
})));
const WalletBottomSheet = lazy(() => import('../components/account/WalletBottomSheet').then(module => ({
  default: module.WalletBottomSheet
})));
const LoyaltyPointsBottomSheet = lazy(() => import('../components/account/LoyaltyPointsBottomSheet').then(module => ({
  default: module.LoyaltyPointsBottomSheet
})));
const LanguageBottomSheet = lazy(() => import('../components/modals/LanguageBottomSheet').then(module => ({
  default: module.LanguageBottomSheet
})));
export function AccountPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    language,
    setLanguage,
    isAuthenticated,
    setAuthenticated
  } = useStore();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [authBottomSheetOpen, setAuthBottomSheetOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [loyaltyBottomSheetOpen, setLoyaltyBottomSheetOpen] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  const [editProfileBottomSheetOpen, setEditProfileBottomSheetOpen] = useState(false);
  const [changePasswordBottomSheetOpen, setChangePasswordBottomSheetOpen] = useState(false);
  const [walletBottomSheetOpen, setWalletBottomSheetOpen] = useState(false);
  const [pointsBottomSheetOpen, setPointsBottomSheetOpen] = useState(false);
  const [languageBottomSheetOpen, setLanguageBottomSheetOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const user = {
    name: 'Ahmed Mohammed',
    email: 'ahmed.mohammed@example.com',
    phone: '+966 50 123 4567',
    avatar: ''
  };
  const userProfileData = {
    name: 'Ahmed Mohammed',
    email: 'ahmed.mohammed@example.com',
    phone: '+966 50 123 4567',
    gender: 'Male',
    maritalStatus: 'Single',
    nationality: 'Saudi Arabian',
    dateOfBirth: '1990-05-15',
    workingSector: 'Technology',
    city: 'Riyadh',
    district: 'Al Olaya',
    preferredLanguage: 'English'
  };
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/addresses')) return 'addresses';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/wallet')) return 'wallet';
    if (path.includes('/points')) return 'points';
    if (path.includes('/wishlist')) return 'wishlist';
    if (path.includes('/returns')) return 'returns';
    if (path.includes('/coupons')) return 'coupons';
    if (path.includes('/payment-requests')) return 'payment-requests';
    return 'login-security';
  };
  const [activeSection, setActiveSection] = useState(getActiveSection());
  useEffect(() => {
    const newSection = getActiveSection();
    if (newSection !== activeSection) {
      setActiveSection(newSection);
    }
    if (window.innerWidth < 768) {
      if (newSection === 'wallet') {
        setWalletBottomSheetOpen(true);
      } else if (newSection === 'points') {
        setPointsBottomSheetOpen(true);
      }
    }
  }, [location]);
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (savedData) {
      const data = JSON.parse(savedData);
      const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
      const completedRequired = requiredFields.filter(field => field !== '').length;
      const progress = Math.round(completedRequired / requiredFields.length * 100);
      setLoyaltyProgress(progress);
      // If profile is complete, set loyalty member data
      if (progress === 100) {
        setLoyaltyData({
          memberId: '1234567890',
          pointsBalance: 3250,
          status: 'active',
          tier: 'gold',
          isEnrolled: true
        });
      } else {
        setLoyaltyData({
          isEnrolled: false
        });
      }
    }
  }, [loyaltyBottomSheetOpen]);
  const getSEOData = () => {
    switch (activeSection) {
      case 'orders':
        return {
          title: 'My Orders',
          description: 'View order status, invoices, and tracking.',
          canonical: '/en/account/orders/'
        };
      case 'wishlist':
        return {
          title: 'Wishlist',
          description: 'Save products you love.',
          canonical: '/en/wishlist/'
        };
      case 'addresses':
        return {
          title: 'My Addresses',
          description: 'Manage your delivery addresses.',
          canonical: '/en/account/addresses/'
        };
      case 'wallet':
        return {
          title: 'My Wallet',
          description: 'View your wallet balance.',
          canonical: '/en/account/wallet/'
        };
      case 'points':
        return {
          title: 'Loyalty Points',
          description: 'Track and redeem your loyalty points.',
          canonical: '/en/account/points/'
        };
      case 'returns':
        return {
          title: 'Returns',
          description: 'Manage your product returns.',
          canonical: '/en/account/returns/'
        };
      case 'coupons':
        return {
          title: 'My Coupons',
          description: 'View and manage your coupons.',
          canonical: '/en/account/coupons/'
        };
      case 'payment-requests':
        return {
          title: 'Payment Requests',
          description: 'View and manage your payment links and pending payments.',
          canonical: '/en/account/payment-requests/'
        };
      default:
        return {
          title: 'My Account',
          description: 'Manage your account.',
          canonical: '/en/account/'
        };
    }
  };
  const seoData = getSEOData();
  const renderSection = () => {
    // Check if we're on a payment request details page
    if (location.pathname.includes('/payment-requests/') && location.pathname.split('/').length > 3) {
      return <PaymentRequestDetailsSection data-id="element-5767" data-mp="1t97skxumi6ld" />;
    }
    switch (activeSection) {
      case 'login-security':
        return <LoginSecuritySection data-id="element-5768" data-mp="20t07f8blf17k" />;
      case 'addresses':
        return <AddressesSection data-id="element-5769" data-mp="20qnvbbsinl2f" />;
      case 'orders':
        return <OrdersSection data-id="element-5770" data-mp="25r1vk7nvlqpw" />;
      case 'wallet':
        return <WalletSection data-id="element-5771" data-mp="25pgwq5uj3lm8" />;
      case 'points':
        return <LoyaltyPointsSection data-id="element-5772" data-mp="2ajl6ewv6i7r0" />;
      case 'wishlist':
        return <WishlistSection data-id="element-5773" data-mp="2fhmww2uxp9ci" />;
      case 'returns':
        return <ReturnsSection data-id="element-5774" data-mp="2fh3jmzg8b94t" />;
      case 'coupons':
        return <CouponsSection data-id="element-5775" data-mp="1hue7hwrtbg58" />;
      case 'payment-requests':
        return <PaymentRequestsSection data-id="element-5776" data-mp="1hvz5xxh8zj8d" />;
      default:
        return <LoginSecuritySection data-id="element-5777" data-mp="1mu0jy3ztgscz" />;
    }
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setAuthenticated(true);
    setAuthBottomSheetOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      setTimeout(() => setLoyaltyBottomSheetOpen(true), 500);
    }
  };
  const handleLoyaltyComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyBottomSheetOpen(false);
    // Update loyalty data to enrolled state
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
  };
  const handleWalletClose = () => {
    setWalletBottomSheetOpen(false);
    navigate('/account');
  };
  const handlePointsClose = () => {
    setPointsBottomSheetOpen(false);
    navigate('/account');
  };
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'ar');
    setLanguageBottomSheetOpen(false);
  };
  const isOnSectionPage = activeSection !== 'login-security';
  return <HelmetProvider data-id="element-5778" data-mp="3a8ilm6y7hauo">
      <SEO title={seoData.title} description={seoData.description} canonical={seoData.canonical} robots="noindex,follow" data-id="element-5779" data-mp="3a7zella3g6vf" />
      <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden" data-id="element-5780" data-mp="3f5it8mapelnw">
        <div className="hidden md:block" data-id="element-5781" data-mp="phs0e2rflqql">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={false} data-id="element-5782" data-mp="uh4i693g47mb" />
        </div>
        <main className="flex-grow overflow-x-hidden pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-5783" data-mp="2a2oorocchk7c">
          <h1 className="sr-only" data-id="element-5784" data-mp="2579e159mw4f5">My Account Dashboard</h1>
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-5785" data-mp="209r7tmn043au">
            <MobileInternalHeader title={isOnSectionPage ? seoData.title : 'My Account'} showMenu={false} data-id="element-5786" data-mp="1w97fcwddhnv1" />
          </div>
          {/* Mobile Content */}
          <div className="md:hidden" data-id="element-5787" data-mp="1ma86pdslxs81">
            {!isOnSectionPage ? <PullToRefresh onRefresh={async () => {
            await new Promise(r => setTimeout(r, 600));
          }} data-id="element-5788" data-mp="1hbohn4ipw0bg">
                <UserProfileCard user={isAuthenticated ? user : undefined} onSignIn={() => {
              setAuthMode('login');
              setAuthBottomSheetOpen(true);
            }} onRegister={() => {
              setAuthMode('register');
              setAuthBottomSheetOpen(true);
            }} onEdit={() => setEditProfileBottomSheetOpen(true)} stats={{
              points: loyaltyData.pointsBalance ?? 3250,
              walletBalance: 'SAR 250',
              orders: 5
            }} tier={loyaltyData.tier ? `${loyaltyData.tier.charAt(0).toUpperCase()}${loyaltyData.tier.slice(1)} Member` : 'Gold Member'} data-id="element-5789" data-mp="3gnzkmphyyy9a" />
                <div className="px-4 mt-5" data-id="element-5790" data-mp="1jpju1a059v2o">
                  <AccountMenuList isAuthenticated={isAuthenticated} onSignIn={() => {
                setAuthMode('login');
                setAuthBottomSheetOpen(true);
              }} onRegister={() => {
                setAuthMode('register');
                setAuthBottomSheetOpen(true);
              }} onLanguageClick={() => setLanguageBottomSheetOpen(true)} onPasswordClick={() => setChangePasswordBottomSheetOpen(true)} data-id="element-5791" data-mp="1jso9x4mz2f96" />
                </div>
                <div className="hidden px-4 mt-4" data-id="element-5792" data-mp="3vza6ffhpc63k">
                  <LoyaltyCard progress={loyaltyProgress} onJoinNow={() => setLoyaltyBottomSheetOpen(true)} onViewDetails={() => setPointsBottomSheetOpen(true)} loyaltyData={loyaltyData} data-id="element-5793" data-mp="erom38suqjqw" />
                </div>
                <div className="hidden px-4 mt-4" data-id="element-5794" data-mp="3da3npsgwrehr">
                  <ReferralCard data-id="element-5795" data-mp="3d77qk6cvwtud" />
                </div>
                <div className="hidden px-4 mt-4" data-id="element-5796" data-mp="3ryqdko45von8">
                  <button onClick={() => setChangePasswordBottomSheetOpen(true)} className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between active:scale-[0.99] hover:border-[#1E2B55] transition" data-id="element-5797" data-mp="3n78btxubdk17">
                    <div className="flex items-center gap-3" data-id="element-5798" data-mp="1xrfrg8xrbz8e">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center" data-id="element-5799" data-mp="1xt0ktv8c5c4o">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-5800" data-mp="21qollewplz4h">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" data-id="element-5801" data-mp="2gigo2qbenios" />
                        </svg>
                      </div>
                      <div className="text-left" data-id="element-5802" data-mp="v4ubp84k2gts">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-5803" data-mp="v65kjmcuyerr">
                          Change Password
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-5804" data-mp="skekhkma6xb8">
                          Update your password
                        </p>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-5805" data-mp="ittmnzeflwtd">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-5806" data-mp="1bhphszocrgo1" />
                    </svg>
                  </button>
                </div>
                <div className="hidden px-4 mt-4" data-id="element-5807" data-mp="xjr7q6h2e6ec">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-5808" data-mp="xlbz5n83197z" />
                </div>
                <div className="hidden px-4 py-4 bg-white border-t border-gray-200 mt-4" data-id="element-5809" data-mp="3i8oyruuewyrb">
                  <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-5810" data-mp="3jqnafhzew4ee">
                    Follow Us
                  </h3>
                  <div className="flex items-center justify-around gap-3" data-id="element-5811" data-mp="3opqdwxzqu2k9">
                    <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-5812" data-mp="39sg5jxiedcsr">
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5813" data-mp="30yl34x8nu0gt">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-5814" data-mp="2r287httk1cjf">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-5815" data-mp="2i0rwpkytv8eo" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-5816" data-mp="1yhb9vgqj5s0a">TikTok</span>
                    </a>
                    <a href="https://www.snapchat.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-5817" data-mp="2ccwtt9hwpllu">
                      <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5818" data-mp="2hd1d17a78ufr">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-5819" data-mp="2r3vy48bplnzm">
                          <path d="M12.206 2.024c-1.013 0-2.022.196-2.982.579a7.532 7.532 0 0 0-2.531 1.672 7.91 7.91 0 0 0-1.713 2.482c-.408.938-.615 1.942-.615 2.982 0 1.228.267 2.403.793 3.493.526 1.09 1.302 2.046 2.305 2.839.265.21.497.446.69.702a3.43 3.43 0 0 1 .446.87c.055.146.089.298.1.452.011.154-.011.308-.066.452l-.497 1.304c-.044.11-.055.232-.033.348.022.116.077.224.16.308.083.084.19.139.308.16.116.022.238.011.348-.033l1.793-.662c.232-.088.485-.11.728-.066.243.044.475.143.675.286.43.308.905.55 1.412.719a6.38 6.38 0 0 0 1.625.254c1.013 0 2.022-.196 2.982-.579a7.532 7.532 0 0 0 2.531-1.672 7.91 7.91 0 0 0 1.713-2.482c.408-.938.615-1.942.615-2.982a7.532 7.532 0 0 0-.615-2.982 7.91 7.91 0 0 0-1.713-2.482 7.532 7.532 0 0 0-2.531-1.672 7.532 7.532 0 0 0-2.982-.579z" data-id="element-5820" data-mp="304lt59t5baca" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-5821" data-mp="6dlstn8s2lpn">Snapchat</span>
                    </a>
                    <a href="https://www.x.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-5822" data-mp="351j3g4us72rx">
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5823" data-mp="2xj1npgwbarqi">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-5824" data-mp="2sindbs78n5pb">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-5825" data-mp="2jk1wc1hezyqn" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-5826" data-mp="24umamzwqqjht">X</span>
                    </a>
                    <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-5827" data-mp="1zv9nee1yjaep">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5828" data-mp="kpprxqqx60ak">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-5829" data-mp="bo00ikx1f1cs">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-5830" data-mp="1sojfvy5pi1v" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-5831" data-mp="1fz36hvs9i6sq">Instagram</span>
                    </a>
                    <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-5832" data-mp="1kr39xy7jjhu2">
                      <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5833" data-mp="1vko9cxako4n8">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-5834" data-mp="1qks6j1dm2j99">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-5835" data-mp="20bwjjvjq7u1g" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-5836" data-mp="1hjtxu5s89nkc">Facebook</span>
                    </a>
                    <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-5837" data-mp="2x0m1tmz9mony">
                      <div className="w-12 h-12 rounded-full bg-blue-700 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5838" data-mp="35inzcsyf8irc">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-5839" data-mp="2qmostmqymqob">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-5840" data-mp="3jakpwskfyklm" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-5841" data-mp="2jc3cs1asu86e">LinkedIn</span>
                    </a>
                  </div>
                </div>
              </PullToRefresh> : <div className="p-4" data-id="element-5842" data-mp="29e77hq4jm7g7">{renderSection()}</div>}
          </div>
          {/* Desktop Content */}
          <div className="hidden md:block" data-id="element-5843" data-mp="24fm6hohgvjqp">
            <AccountLayout activeSection={activeSection} data-id="element-5844" data-mp="321jqjqbhvx4r">{isOnSectionPage ? renderSection() : <div className="mx-auto max-w-2xl space-y-5" data-id="element-5845" data-mp="323nsw3tqwo2x"><UserProfileCard user={isAuthenticated ? user : undefined} onSignIn={() => {
                setAuthMode('login');
                setAuthBottomSheetOpen(true);
              }} onRegister={() => {
                setAuthMode('register');
                setAuthBottomSheetOpen(true);
              }} onEdit={() => setEditProfileBottomSheetOpen(true)} stats={{
                points: loyaltyData.pointsBalance ?? 3250,
                walletBalance: 'SAR 250',
                orders: 5
              }} tier={loyaltyData.tier ? `${loyaltyData.tier.charAt(0).toUpperCase()}${loyaltyData.tier.slice(1)} Member` : 'Gold Member'} data-id="element-5846" data-mp="2x3rnq19baqls" /><AccountMenuList isAuthenticated={isAuthenticated} onSignIn={() => {
                setAuthMode('login');
                setAuthBottomSheetOpen(true);
              }} onRegister={() => {
                setAuthMode('register');
                setAuthBottomSheetOpen(true);
              }} onLanguageClick={() => setLanguageBottomSheetOpen(true)} onPasswordClick={() => setChangePasswordBottomSheetOpen(true)} data-id="element-5847" data-mp="3oai5dqz4mlzw" /></div>}</AccountLayout>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-5848" data-mp="b3oqe2g12ke8">
          <Footer data-id="element-5849" data-mp="b2nczrbjhltc" />
        </div>
        <MobileFooterNav data-id="element-5850" data-mp="b1m51vptlffu" />
        <PharmacistChat data-id="element-5851" data-mp="f51yykai91ou" />
        <div className="h-16 md:hidden" data-id="element-5852" data-mp="f4ifgz57i58p"></div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-5853" data-mp="f37awx3xstgj" />
        {authBottomSheetOpen && <Suspense fallback={null} data-id="element-5854" data-mp="tw157onvbe59"><AuthBottomSheet isOpen initialTab={authMode} onClose={() => setAuthBottomSheetOpen(false)} onAuthSuccess={handleAuthSuccess} data-id="element-5855" data-mp="tsdcgrq8yvrl" /></Suspense>}
        {loyaltyBottomSheetOpen && <Suspense fallback={null} data-id="element-5856" data-mp="95f0fh37xseo"><LoyaltyOnboardingBottomSheet isOpen onClose={() => setLoyaltyBottomSheetOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-5857" data-mp="462gb43qezfs" /></Suspense>}
        {editProfileBottomSheetOpen && <Suspense fallback={null} data-id="element-5858" data-mp="iw9qyxnhze5x"><EditProfileBottomSheet isOpen onClose={() => setEditProfileBottomSheetOpen(false)} userInfo={userProfileData} onSave={data => console.log('Saving profile:', data)} data-id="element-5859" data-mp="e48hfjhps1jl" /></Suspense>}
        {changePasswordBottomSheetOpen && <Suspense fallback={null} data-id="element-5860" data-mp="mx20zt471n39"><ChangePasswordBottomSheet isOpen onClose={() => setChangePasswordBottomSheetOpen(false)} onSubmit={data => console.log('Changing password:', data)} data-id="element-5861" data-mp="mvqv9nr6u59i" /></Suspense>}
        {walletBottomSheetOpen && <Suspense fallback={null} data-id="element-5862" data-mp="ww1a1csvfkqs"><WalletBottomSheet isOpen onClose={handleWalletClose} data-id="element-5863" data-mp="wvholm40ie80" /></Suspense>}
        {pointsBottomSheetOpen && <Suspense fallback={null} data-id="element-5864" data-mp="33na630iftpkz"><LoyaltyPointsBottomSheet isOpen onClose={handlePointsClose} data-id="element-5865" data-mp="2ym3096zuv0gy" /></Suspense>}
        {languageBottomSheetOpen && <Suspense fallback={null} data-id="element-5866" data-mp="1e4ahmn822asz"><LanguageBottomSheet isOpen onClose={() => setLanguageBottomSheetOpen(false)} currentLanguage={language} onLanguageChange={handleLanguageChange} data-id="element-5867" data-mp="1e53khd3w8g5u" /></Suspense>}
      </div>
    </HelmetProvider>;
}